import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Errores de red/timeout/cancelación (sin respuesta del server) NO deben cerrar
    // sesión: pasan típicamente en el reload por pull-to-refresh en móvil.
    if (!error.response) {
      return Promise.reject(error);
    }
    const status = error.response.status;
    const url = error.config?.url || '';
    // El chequeo inicial de sesión (/auth/me) lo maneja AuthContext, para no
    // desloguear por un fallo puntual durante la carga.
    if (status === 401 && !url.includes('/auth/me')) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.patch('/auth/me', data),
  processSession: (sessionId) => api.get('/auth/session', {
    headers: { 'X-Session-ID': sessionId }
  }),
};

// Admin de usuarios INTERNOS (solo rol=admin)
export const usersAdminApi = {
  list: () => api.get('/admin/users'),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.patch(`/admin/users/${id}`, data),
  toggleActive: (id, activo) => api.post(`/admin/users/${id}/toggle-active`, { activo }),
  resetPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  audit: (limit = 100) => api.get('/admin/users/audit', { params: { limit } }),
};

// Edificios API
export const edificiosApi = {
  list: () => api.get('/edificios'),
  get: (id) => api.get(`/edificios/${id}`),
  create: (data) => api.post('/edificios', data),
  update: (id, data) => api.put(`/edificios/${id}`, data),
  delete: (id) => api.delete(`/edificios/${id}`),
};

// Propietarios API
export const propietariosApi = {
  list: () => api.get('/propietarios'),
  get: (id) => api.get(`/propietarios/${id}`),
  create: (data) => api.post('/propietarios', data),
  update: (id, data) => api.put(`/propietarios/${id}`, data),
  delete: (id) => api.delete(`/propietarios/${id}`),
  resetPassword: (id) => api.post(`/propietarios/${id}/reset-password`),
  // Import/Export Excel
  exportarExcel: () => `${API_BASE}/propietarios/exportar-excel`,
  importarExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/propietarios/importar-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Unidades API
export const unidadesApi = {
  list: (params) => api.get('/unidades', { params }),
  updateCapacidad: (unidadId, data) => api.put(`/unidades/${unidadId}/capacidad`, data),
  get: (id) => api.get(`/unidades/${id}`),
  create: (data) => api.post('/unidades', data),
  createMasivo: (data) => api.post('/unidades/masivo', data),
  createAltaRapida: (data) => api.post('/unidades/alta-rapida', data),
  update: (id, data) => api.put(`/unidades/${id}`, data),
  updateAdministracion: (id, data) => api.put(`/unidades/${id}/administracion`, data),
  bulkUpdateAdministracion: (data) => api.post(`/unidades/bulk-update-administracion`, data),
  bulkToggleAdmin: (unidad_ids, en_administracion) =>
    api.post('/unidades/bulk-toggle-admin', { unidad_ids, en_administracion }),
  updatePropietario: (id, data) => api.put(`/unidades/${id}/propietario`, data),
  updateAlquiler: (id, data) => api.put(`/unidades/${id}/alquiler`, data),
  uploadContrato: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/unidades/${id}/contrato`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadContratoAdministracion: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/unidades/${id}/contrato-administracion`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadContratoAdministracionUrl: (id) => `${API_BASE}/unidades/${id}/contrato-administracion/download`,
  deleteContratoAdministracion: (id) => api.delete(`/unidades/${id}/contrato-administracion`),
  analizarContrato: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/unidades/${id}/analizar-contrato`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteContrato: (id) => api.delete(`/unidades/${id}/contrato`),
  downloadContrato: (id) => `${BACKEND_URL}/api/unidades/${id}/contrato`,
  uploadReserva: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/unidades/${id}/reserva`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteReserva: (id) => api.delete(`/unidades/${id}/reserva`),
  downloadReserva: (id) => {
    const token = localStorage.getItem('auth_token');
    return `${BACKEND_URL}/api/unidades/${id}/reserva?token=${token}`;
  },
  delete: (id) => api.delete(`/unidades/${id}`),
  getAlquilerActualizado: (id, periodo) => api.get(`/unidades/${id}/alquiler-actualizado`, { params: { periodo } }),
  getHistorial: (id) => api.get(`/unidades/${id}/historial`),
};

// Inquilinos (Foresta) — acceso a plataforma vinculado a contratos vigentes
export const inquilinosApi = {
  listByEdificio: (edificioId) => api.get(`/inquilinos/edificio/${edificioId}`),
  getAcceso: (contratoId) => api.get(`/contratos/${contratoId}/acceso-inquilino`),
  crearAcceso: (contratoId, data) => api.post(`/contratos/${contratoId}/acceso-inquilino`, data),
  editarAcceso: (contratoId, data) => api.put(`/contratos/${contratoId}/acceso-inquilino`, data),
  resetAcceso: (contratoId) => api.post(`/contratos/${contratoId}/acceso-inquilino/reset`),
  deshabilitarAcceso: (contratoId) => api.delete(`/contratos/${contratoId}/acceso-inquilino`),
  // Link GENERAL de activación (igual que propietarios). El inquilino se identifica con su email.
  getActivationLink: () => {
    const base = process.env.REACT_APP_APP_PUBLIC_URL || window.location.origin;
    return `${base}/activar-inquilino`;
  },
  // Mensaje pre-armado para copiar y enviar al inquilino (WhatsApp / email).
  getActivationMessage: () => {
    return `Hola! 👋

Te comparto el acceso a la app de Global Citizen

🔗 https://app.gcitizen.com.ar/activar-inquilino

Desde ahí vas a poder ver tu expensas

📲 Para dejarla instalada como app en el celular:

✅ Android:
Abrilo con Chrome, tocá los 3 puntitos y elegí "Agregar a pantalla principal" o "Instalar app".

✅ iPhone:
Abrilo con Safari, tocá Compartir y elegí "Agregar a pantalla de inicio".

Así te queda el ícono en el celular y podés entrar como una app más.`;
  },
};

// Reservas (pre-contratos) — Foresta / TE
export const reservasApi = {
  list: (params = {}) => api.get('/reservas', { params }),
  create: (data) => api.post('/reservas', data),
  update: (id, data) => api.put(`/reservas/${id}`, data),
  delete: (id) => api.delete(`/reservas/${id}`),
  uploadArchivo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/reservas/${id}/archivo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadArchivoUrl: (id) => {
    const token = localStorage.getItem('auth_token');
    return `${BACKEND_URL}/api/reservas/${id}/archivo?token=${token}`;
  },
  completar: (id, contrato_id) => api.post(`/reservas/${id}/completar`, null, { params: { contrato_id } }),
};

// Historial API
export const historialApi = {
  getByEdificio: (edificioId) => api.get(`/edificios/${edificioId}/historial`),
};

// Grupos de Unidades API
export const coworkSuscriptoresApi = {
  list: (edificioId) => api.get('/cowork-suscriptores', { params: { edificio_id: edificioId } }),
  disponibles: (edificioId) => api.get('/cowork-suscriptores/disponibles', { params: { edificio_id: edificioId } }),
  create: (data) => api.post('/cowork-suscriptores', data),
  update: (id, data) => api.put(`/cowork-suscriptores/${id}`, data),
  finalizar: (id) => api.post(`/cowork-suscriptores/${id}/finalizar`),
  eliminar: (id) => api.delete(`/cowork-suscriptores/${id}`),
};


export const gruposUnidadesApi = {
  list: (edificioId) => api.get('/grupos-unidades', { params: { edificio_id: edificioId } }),
  create: (data) => api.post('/grupos-unidades', data),
  update: (id, data) => api.put(`/grupos-unidades/${id}`, data),
  delete: (id) => api.delete(`/grupos-unidades/${id}`),
};

// Reordenamiento de filas
export const reordenarApi = {
  moverFila: (edificioId, tipo, id, direccion) => 
    api.post(`/edificios/${edificioId}/reordenar`, { tipo, id, direccion }),
};

// Cotizaciones API
export const cotizacionesApi = {
  list: () => api.get('/cotizaciones'),
  get: (periodo) => api.get(`/cotizaciones/${periodo}`),
  createOrUpdate: (data) => api.post('/cotizaciones', data),
  delete: (id) => api.delete(`/cotizaciones/${id}`),
};

export const indicesApi = {
  getIPC: () => api.get('/indices/ipc'),
  syncIPC: () => api.post('/indices/sync-ipc'),
};

// Resultados API
export const resultadosApi = {
  list: (params) => api.get('/resultados', { params }),
  get: (id) => api.get(`/resultados/${id}`),
  create: (data) => api.post('/resultados', data),
  createBatch: (data) => api.post('/resultados/batch', data),
  update: (id, data) => api.put(`/resultados/${id}`, data),
  publicar: (id) => api.post(`/resultados/${id}/publicar`),
  cambiarEstado: (id, estado) => api.patch(`/resultados/${id}/estado`, null, { params: { estado } }),
  cambiarEstadoGrupo: (grupoId, estado, periodo) => api.patch(`/resultados/grupo/${grupoId}/estado`, null, { params: { estado, periodo } }),
  delete: (id) => api.delete(`/resultados/${id}`),
  uploadFacturaPropietario: (resultadoId, formData) => {
    return api.post(`/resultados/${resultadoId}/factura-propietario`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  procesarFactura: (id) => api.post(`/resultados/${id}/procesar-factura`),
  uploadFacturaPropietarioAdmin: (resultadoId, formData, propietarioId) => {
    return api.post(`/resultados/${resultadoId}/factura-propietario-admin`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: propietarioId ? { propietario_id: propietarioId } : {},
    });
  },
  downloadFacturaPropietario: (resultadoId, propietarioId) => {
    const token = localStorage.getItem('auth_token');
    const pid = propietarioId ? `&propietario_id=${encodeURIComponent(propietarioId)}` : '';
    return `${API_BASE}/archivos/factura-propietario/${resultadoId}?token=${token}${pid}`;
  },
  // Egresos
  getEgresos: (edificioId, periodo) => api.get(`/egresos/${edificioId}`, { params: { periodo } }),
  getEgresosUnidad: (unidadId, periodo) => api.get(`/egresos/unidad/${unidadId}`, { params: { periodo } }),
  saveEgresos: (edificioId, unidadId, periodo, data) => api.post(`/egresos/${edificioId}/${unidadId}`, data, { params: { periodo } }),
  syncExpensasEgresos: (edificioId, periodo) => api.post(`/sync-expensas-egresos/${edificioId}`, {}, { params: { periodo } }),
};

export const pagosApi = {
  get: (edificioId, periodo) => api.get(`/pagos/${edificioId}`, { params: { periodo } }),
  update: (edificioId, unidadId, periodo, estado, propietarioId, fecha) => api.patch(`/pagos/${edificioId}/${unidadId}`, null, { params: { periodo, estado, propietario_id: propietarioId, ...(fecha ? { fecha } : {}) } }),
  uploadComprobante: (edificioId, unidadId, periodo, propietarioId, formData) => api.post(`/pagos/${edificioId}/${unidadId}/comprobante`, formData, { params: { periodo, propietario_id: propietarioId }, headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadComprobanteUrl: (edificioId, unidadId, periodo, propietarioId) => `${API_BASE}/pagos/${edificioId}/${unidadId}/comprobante?periodo=${periodo}&propietario_id=${propietarioId}`,
  recuperarArchivos: () => api.post(`/pagos/recuperar-archivos-huerfanos`),
};

export const detalleGastosApi = {
  getPlantilla: (edificioId) => api.get(`/detalle-gastos/plantilla/${edificioId}`),
  savePlantilla: (edificioId, data) => api.put(`/detalle-gastos/plantilla/${edificioId}`, data),
  getValores: (edificioId, periodos) => api.get(`/detalle-gastos/${edificioId}`, { params: { periodos: periodos.join(',') } }),
  saveValores: (edificioId, periodo, valores) => api.put(`/detalle-gastos/${edificioId}/${periodo}`, { valores }),
};

export const comisionesApi = {
  list: (params) => api.get('/comisiones', { params }),
  resumen: (params) => api.get('/comisiones/resumen', { params }),
  reporte: (params) => api.get('/comisiones/reporte', { params }),
  update: (comisionId, data) => api.put(`/comisiones/${comisionId}`, data),
  solicitar: (comisionId) => api.put(`/comisiones/${comisionId}/solicitar`),
  deshacer: (comisionId) => api.put(`/comisiones/${comisionId}/deshacer`),
};

export const documentosApi = {
  list: () => api.get('/documentos'),
  create: (titulo, file) => {
    const fd = new FormData();
    fd.append('titulo', titulo);
    fd.append('file', file);
    return api.post('/documentos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadVersion: (documentoId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/documentos/${documentoId}/version`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadUrl: (documentoId, version = null) => {
    const token = localStorage.getItem('auth_token');
    const v = version != null ? `&version=${version}` : '';
    return `${BACKEND_URL}/api/documentos/${documentoId}/download?token=${token}${v}`;
  },
  delete: (documentoId) => api.delete(`/documentos/${documentoId}`),
};

export const expensasApi = {
  // Edificios bajo consorcio
  listEdificios: () => api.get('/expensas/edificios'),

  // Configuración
  getConfig: (edificioId) => api.get(`/expensas/edificios/${edificioId}/configuracion`),
  upsertConfig: (edificioId, data) => api.put(`/expensas/edificios/${edificioId}/configuracion`, data),

  // Plan de cuentas
  listPlanCuentas: (edificioId) => api.get(`/expensas/edificios/${edificioId}/plan-cuentas`),
  upsertCuenta: (edificioId, data) => api.post(`/expensas/edificios/${edificioId}/plan-cuentas`, data),
  deleteCuenta: (edificioId, codigo) => api.delete(`/expensas/edificios/${edificioId}/plan-cuentas/${codigo}`),
  bulkPlan: (edificioId, cuentas) => api.put(`/expensas/edificios/${edificioId}/plan-cuentas-bulk`, { cuentas }),
  copyPlanFrom: (edificioId, sourceEdificioId, overwrite = false) =>
    api.post(`/expensas/edificios/${edificioId}/plan-cuentas/copiar-desde`,
      { source_edificio_id: sourceEdificioId, overwrite_existing: overwrite }),

  // Participación
  getParticipacion: (edificioId) => api.get(`/expensas/edificios/${edificioId}/participacion`),
  saveParticipacion: (edificioId, items) => api.put(`/expensas/edificios/${edificioId}/participacion`, { items }),

  // Períodos
  listPeriodos: (edificioId) => api.get(`/expensas/edificios/${edificioId}/periodos`),
  getOrCreatePeriodoPorMes: (edificioId, mes) => api.get(`/expensas/edificios/${edificioId}/periodo-por-mes/${mes}`),
  createPeriodo: (edificioId, periodo) => api.post(`/expensas/edificios/${edificioId}/periodos`, { periodo }),
  getPeriodo: (edificioId, periodoId) => api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}`),
  deletePeriodo: (edificioId, periodoId) => api.delete(`/expensas/edificios/${edificioId}/periodos/${periodoId}`),
  importarColppy: (edificioId, periodoId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/expensas/edificios/${edificioId}/periodos/${periodoId}/importar-colppy`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMovimientos: (edificioId, periodoId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/movimientos`),
  eliminarImportacion: (edificioId, periodoId) =>
    api.delete(`/expensas/edificios/${edificioId}/periodos/${periodoId}/importacion`),
  getCalculo: (edificioId, periodoId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/calculo`),
  getApertura: (edificioId, periodoId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/apertura`),
  getResumenGastos: (edificioId, periodoId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/resumen-gastos`),
  getDetalleSubrubro: (edificioId, periodoId, rubro, subrubro, periodo) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/detalle-subrubro`, { params: { rubro, subrubro, ...(periodo ? { periodo } : {}) } }),
  upsertResumenComentario: (edificioId, periodoId, payload) =>
    api.put(`/expensas/edificios/${edificioId}/periodos/${periodoId}/resumen-gastos/comentario`, payload),
  getLiquidacionesComparativa: (edificioId, periodoId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/liquidaciones-comparativa`),
  listPropietariosEdificio: (edificioId) =>
    api.get(`/expensas/edificios/${edificioId}/propietarios`),
  getVistaInversor: (edificioId, periodoId, propietarioId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/vista-inversor/${propietarioId}`),
  getUnidadesSelector: (edificioId, periodoId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/unidades-selector`),
  getVistaUnidad: (edificioId, periodoId, unidadId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/vista-unidad/${unidadId}`),
  getVistaUnidadPDF: (edificioId, periodoId, unidadId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/vista-unidad/${unidadId}/pdf`, { responseType: 'blob' }),
  getConstanciaDeudaPDF: (edificioId, unidadId) =>
    api.get(`/expensas/edificios/${edificioId}/unidades/${unidadId}/constancia-deuda/pdf`, { responseType: 'blob' }),
  getVistaOperador: (edificioId, periodoId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/vista-operador`),
  getMiVistaExpensas: (unidadId, periodo) =>
    api.get(`/expensas/mi-unidad/${unidadId}/vista-expensas`, { params: periodo ? { periodo } : {} }),
  getMisPeriodosExpensas: (unidadId) =>
    api.get(`/expensas/mi-unidad/${unidadId}/periodos-disponibles`),
  getExpensasResumenPorMes: (edificioId, periodo) =>
    api.get(`/expensas/edificios/${edificioId}/por-mes/${periodo}/expensas-resumen`),
  aprobarPeriodo: (edificioId, periodoId) =>
    api.post(`/expensas/edificios/${edificioId}/periodos/${periodoId}/aprobar`),
  revertirPeriodo: (edificioId, periodoId) =>
    api.post(`/expensas/edificios/${edificioId}/periodos/${periodoId}/revertir`),
  listLiquidaciones: (edificioId, periodoId) =>
    api.get(`/expensas/edificios/${edificioId}/periodos/${periodoId}/liquidaciones`),

  // Vista inquilino/propietario
  misLiquidaciones: () => api.get('/expensas/mis-liquidaciones'),
  detalleLiquidacion: (liquidacionId) => api.get(`/expensas/liquidaciones/${liquidacionId}/detalle`),
};

// ============================================================
// Cobros de Expensas (V3: 1 fila = 1 unidad × 1 período)
// ============================================================
export const cobrosExpensasApi = {
  snapshot: (edificioId, periodoId, dryRun = false) =>
    api.post(`/expensas-cobros/edificios/${edificioId}/periodos/${periodoId}/snapshot`, null, {
      params: { dry_run: dryRun },
    }),
  list: (edificioId, periodoId, params = {}) =>
    api.get(`/expensas-cobros/edificios/${edificioId}/periodos/${periodoId}`, { params }),
  kpis: (edificioId, periodoId) =>
    api.get(`/expensas-cobros/edificios/${edificioId}/periodos/${periodoId}/kpis`),
  morosos: (edificioId, periodoId) =>
    api.get(`/expensas-cobros/edificios/${edificioId}/periodos/${periodoId}/morosos`),
  getUnidad: (edificioId, periodoId, unidadId) =>
    api.get(`/expensas-cobros/edificios/${edificioId}/periodos/${periodoId}/unidades/${unidadId}`),
  registrarPago: (edificioId, periodoId, unidadId, data) =>
    api.post(`/expensas-cobros/edificios/${edificioId}/periodos/${periodoId}/unidades/${unidadId}/pago`, data),
  editarDeudaAnterior: (edificioId, periodoId, unidadId, data) =>
    api.put(`/expensas-cobros/edificios/${edificioId}/periodos/${periodoId}/unidades/${unidadId}/deuda-anterior`, data),
  anularPago: (edificioId, periodoId, unidadId, pagoId, motivo) =>
    api.delete(`/expensas-cobros/edificios/${edificioId}/periodos/${periodoId}/unidades/${unidadId}/pago/${pagoId}`, {
      params: motivo ? { motivo } : undefined,
    }),
  // Pagos informados por el propietario (Etapa 2)
  listPagosInformados: (params = {}) => api.get('/expensas-cobros/pagos-informados', { params }),
  countPagosInformados: (params = {}) => api.get('/expensas-cobros/pagos-informados/count', { params }),
  aprobarPagoInformado: (id) => api.post(`/expensas-cobros/pagos-informados/${id}/aprobar`),
  rechazarPagoInformado: (id, motivo) => api.post(`/expensas-cobros/pagos-informados/${id}/rechazar`, { motivo }),
  pedirCorreccionPagoInformado: (id, motivo) => api.post(`/expensas-cobros/pagos-informados/${id}/correccion`, { motivo }),
};

// Estado de cuenta + informar pago de expensas (Portal propietario)
export const expensasPortalApi = {
  estadoCuenta: (unidadId) => api.get(`/expensas-portal/unidades/${unidadId}/estado-cuenta`),
  misUnidades: () => api.get('/expensas-portal/mis-unidades'),
  informarPago: (unidadId, formData) =>
    api.post(`/expensas-portal/unidades/${unidadId}/informar-pago`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  comprobanteUrl: (id) => `${API_BASE}/expensas-portal/comprobante/${id}`,
  comprobante: (id) => api.get(`/expensas-portal/comprobante/${id}`, { responseType: 'blob' }),
  constanciaDeuda: (unidadId) =>
    api.get(`/expensas-portal/unidades/${unidadId}/constancia-deuda/pdf`, { responseType: 'blob' }),
};

export const operacionesApi = {
  // Salas
  listSalas: (edificio_id) => api.get('/operaciones/salas', { params: edificio_id ? { edificio_id } : {} }),
  createSala: (data) => api.post('/operaciones/salas', data),
  updateSala: (sala_id, data) => api.put(`/operaciones/salas/${sala_id}`, data),
  deleteSala: (sala_id) => api.delete(`/operaciones/salas/${sala_id}`),
  // Config
  getConfig: (edificio_id) => api.get(`/operaciones/config/${edificio_id}`),
  upsertConfig: (edificio_id, data) => api.put(`/operaciones/config/${edificio_id}`, data),
  // Límites
  listLimites: (edificio_id) => api.get('/operaciones/limites', { params: { edificio_id } }),
  createLimite: (data) => api.post('/operaciones/limites', data),
  deleteLimite: (limite_id) => api.delete(`/operaciones/limites/${limite_id}`),
  // Usuarios edificio
  listUsuariosEdificio: (params = {}) => api.get('/operaciones/usuarios-edificio', { params }),
  crearMaster: (unidad_id, data) => api.post(`/operaciones/unidades/${unidad_id}/master`, data),
  crearAutorizado: (unidad_id, data) => api.post(`/operaciones/unidades/${unidad_id}/autorizados`, data),
  actualizarUsuario: (usuario_edif_id, data) => api.put(`/operaciones/usuarios-edificio/${usuario_edif_id}`, data),
  eliminarUsuario: (usuario_edif_id) => api.delete(`/operaciones/usuarios-edificio/${usuario_edif_id}`),
  resetPassword: (usuario_edif_id) => api.post(`/operaciones/usuarios-edificio/${usuario_edif_id}/reset-password`),
  // Reservas
  listReservas: (params = {}) => api.get('/operaciones/reservas', { params }),
  crearReserva: (data) => api.post('/operaciones/reservas', data),
  cancelarReserva: (reserva_id) => api.delete(`/operaciones/reservas/${reserva_id}`),
  misReservas: () => api.get('/operaciones/mis-reservas'),
  miUnidadResumen: () => api.get('/operaciones/mi-unidad-resumen'),
  // Master desde propietario existente
  listPropietariosDisponibles: (edificio_id) =>
    api.get(`/operaciones/edificios/${edificio_id}/propietarios-disponibles`),
  crearMasterFromPropietario: (unidad_id, data) =>
    api.post(`/operaciones/unidades/${unidad_id}/master/from-propietario`, data),
  // Incidencias
  crearIncidencia: (data) => api.post('/operaciones/incidencias', data),
  listIncidencias: (params = {}) => api.get('/operaciones/incidencias', { params }),
  misIncidencias: () => api.get('/operaciones/mis-incidencias'),
  cambiarEstadoIncidencia: (incidencia_id, data) => api.patch(`/operaciones/incidencias/${incidencia_id}/estado`, data),
  historialIncidenciasUnidad: (unidad_id) => api.get(`/operaciones/unidades/${unidad_id}/incidencias-historial`),
};

export const revisorApi = {
  comparativa: (edificioId, concepto = 'ingresos_totales', meses = 3) =>
    api.get(`/revisor/edificios/${edificioId}/comparativa`, { params: { concepto, meses } }),
  facturacionCobro: (edificioId, meses = 3) =>
    api.get(`/revisor/edificios/${edificioId}/facturacion-cobro`, { params: { meses } }),
  renta: (edificioId, meses = 4) =>
    api.get(`/revisor/edificios/${edificioId}/renta`, { params: { meses } }),
  comisiones: (edificioId, meses = 4) =>
    api.get(`/revisor/edificios/${edificioId}/comisiones`, { params: { meses } }),
  pagoRenta: (edificioId, periodo = null) =>
    api.get(`/revisor/edificios/${edificioId}/pago-renta`, { params: periodo ? { periodo } : {} }),
  pagoRentaHistorial: (edificioId, meses = 6) =>
    api.get(`/revisor/edificios/${edificioId}/pago-renta/historial`, { params: { meses } }),
  pagoRentaAccion: (edificioId, unidadId, payload) =>
    api.patch(`/revisor/edificios/${edificioId}/pago-renta/${unidadId}`, payload),
};


export const misDatosApi = {
  get: () => api.get('/mis-datos'),
  update: (data) => api.put('/mis-datos', data),
};

// Reclamos y Sugerencias (portal propietario)
export const reclamosApi = {
  opciones: () => api.get('/reclamos/opciones'),
  misUnidades: () => api.get('/reclamos/mis-unidades'),
  list: () => api.get('/reclamos'),
  detalle: (id) => api.get(`/reclamos/${id}`),
  crear: (data) => api.post('/reclamos', data),
  comentar: (id, data) => api.post(`/reclamos/${id}/comentarios`, data),
  upload: (formData) => api.post('/reclamos/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadUrl: (fileId) => `${API_BASE}/reclamos/archivos/${fileId}/download`,
  download: (fileId) => api.get(`/reclamos/archivos/${fileId}/download`, { responseType: 'blob' }),
};

// Reclamos — bandeja interna (revisor / admin)
export const reclamosAdminApi = {
  list: (params = {}) => api.get('/reclamos-admin', { params }),
  meta: () => api.get('/reclamos-admin/meta'),
  nuevosCount: () => api.get('/reclamos-admin/nuevos-count'),
  detalle: (id) => api.get(`/reclamos-admin/${id}`),
  comentar: (id, data) => api.post(`/reclamos-admin/${id}/comentarios`, data),
  cambiarEstado: (id, estado) => api.patch(`/reclamos-admin/${id}/estado`, { estado }),
  upload: (formData) => api.post('/reclamos/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download: (fileId) => api.get(`/reclamos/archivos/${fileId}/download`, { responseType: 'blob' }),
};

// Novedades (backoffice revisor/admin + portal propietario)
export const novedadesApi = {
  list: (params = {}) => api.get('/novedades', { params }),
  crear: (data) => api.post('/novedades', data),
  actualizar: (id, data) => api.put(`/novedades/${id}`, data),
  cambiarEstado: (id, estado) => api.patch(`/novedades/${id}/estado`, { estado }),
  eliminar: (id) => api.delete(`/novedades/${id}`),
  upload: (formData) => api.post('/novedades/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  portal: () => api.get('/novedades/portal'),
  downloadUrl: (fileId) => `${API_BASE}/novedades/archivos/${fileId}/download`,
  download: (fileId) => api.get(`/novedades/archivos/${fileId}/download`, { responseType: 'blob' }),
};

// Facturas Pendientes API
export const facturasPendientesApi = {
  list: () => api.get('/facturas-pendientes'),
};

// Archivos API
export const archivosApi = {
  uploadResultado: (resultadoId, tipoArchivo, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/archivos/resultado/${resultadoId}/${tipoArchivo}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadContrato: (unidadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/archivos/contrato/${unidadId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadResultado: (resultadoId, tipoArchivo) => 
    `${API_BASE}/archivos/download/${resultadoId}/${tipoArchivo}`,
  deleteResultado: (resultadoId, tipoArchivo) =>
    api.delete(`/archivos/${resultadoId}/${tipoArchivo}`),
  downloadContrato: (unidadId) => 
    `${API_BASE}/archivos/contrato/${unidadId}`,
};

// Reportes API
export const reportesApi = {
  edificio: (edificioId, params) => api.get(`/reportes/edificio/${edificioId}`, { params }),
  unidades: (params) => api.get('/reportes/unidades', { params }),
};

// Dashboard API
export const dashboardApi = {
  feesEvolucion: (edificioId) => api.get('/dashboard/fees-evolucion', { params: { edificio_id: edificioId } }),
  kpis: (edificioId) => api.get('/dashboard/kpis', { params: { edificio_id: edificioId } }),
  depositosResumen: (edificioId) => api.get('/dashboard/depositos-resumen', { params: { edificio_id: edificioId } }),
};

// Foresta Ops API (modelo propietario_factura_directo)
export const forestaOpsApi = {
  // Facturas del propietario al inquilino
  listFacturas: (edificioId, periodo) => api.get(`/facturas-propietario/${edificioId}`, { params: { periodo } }),
  uploadFactura: (edificioId, unidadId, periodo, file, extra = {}) => {
    const fd = new FormData();
    fd.append('periodo', periodo);
    fd.append('file', file);
    if (extra.monto != null) fd.append('monto', extra.monto);
    if (extra.fecha_emision) fd.append('fecha_emision', extra.fecha_emision);
    if (extra.numero_factura) fd.append('numero_factura', extra.numero_factura);
    return api.post(`/facturas-propietario/${edificioId}/${unidadId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  patchFactura: (edificioId, unidadId, periodo, data) => api.patch(`/facturas-propietario/${edificioId}/${unidadId}`, data, { params: { periodo } }),

  // Comprobantes del inquilino al propietario
  listComprobantes: (edificioId, periodo) => api.get(`/comprobantes-inquilino/${edificioId}`, { params: { periodo } }),
  uploadComprobante: (edificioId, unidadId, periodo, file, extra = {}) => {
    const fd = new FormData();
    fd.append('periodo', periodo);
    fd.append('file', file);
    if (extra.monto != null) fd.append('monto', extra.monto);
    if (extra.fecha_pago) fd.append('fecha_pago', extra.fecha_pago);
    return api.post(`/comprobantes-inquilino/${edificioId}/${unidadId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  patchComprobante: (edificioId, unidadId, periodo, data) => api.patch(`/comprobantes-inquilino/${edificioId}/${unidadId}`, data, { params: { periodo } }),

  // Control mensual
  listControl: (edificioId, periodo) => api.get(`/control-mensual/${edificioId}`, { params: { periodo } }),
  upsertControl: (edificioId, unidadId, periodo, data) => api.put(`/control-mensual/${edificioId}/${unidadId}`, data, { params: { periodo } }),

  // Pendientes
  listPendientes: (edificioId, periodo) => api.get(`/pendientes/${edificioId}`, { params: { periodo } }),

  // Alquiler mensual (vista unificada: revisión GC + factura + comprobante)
  listAlquilerMensual: (edificioId, periodo) => api.get(`/alquiler-mensual/${edificioId}`, { params: { periodo } }),
  upsertRevision: (edificioId, unidadId, periodo, data) => api.post(`/alquiler-mensual/${edificioId}/${unidadId}/revision`, data, { params: { periodo } }),

  // Morosos
  listMorosos: (edificioId, periodo, mesesAtras = 12) => api.get(`/morosos-foresta/${edificioId}`, { params: { periodo, meses_atras: mesesAtras } }),
  excluirMoroso: (edificioId, unidadId, periodo) => api.delete(`/morosos-foresta/${edificioId}/items/${unidadId}/${periodo}`),
  limpiarMorosos: (edificioId) => api.post(`/morosos-foresta/${edificioId}/limpiar`),

  // Cobros Foresta (control de pagos inquilino → propietario)
  listCobros: (edificioId, periodo) => api.get(`/cobros-foresta/${edificioId}`, { params: { periodo } }),

  // Expensas por unidad
  listExpensasUnidad: (edificioId, periodo) => api.get(`/expensas-por-unidad/${edificioId}`, { params: { periodo } }),
  patchExpensaUnidad: (edificioId, unidadId, periodo, data) => api.patch(`/expensas-por-unidad/${edificioId}/${unidadId}`, data, { params: { periodo } }),

  // Descarga de archivos
  archivoUrl: (archivoId) => `${API_BASE}/foresta-ops/archivo/${archivoId}`,
};

// Facturación API
export const facturacionApi = {
  getMensual: (edificioId, periodo) => api.get(`/facturacion/${edificioId}`, { params: { periodo } }),
  updateStatus: (edificioId, unidadId, periodo, data) => api.put(`/facturacion/${edificioId}/${unidadId}`, data, { params: { periodo } }),
  getIndicadores: (edificioId) => api.get(`/facturacion/${edificioId}/indicadores`),
  getMorosos: (edificioId) => api.get(`/facturacion/${edificioId}/morosos`),
  getEmailConfig: () => api.get('/facturacion/email-config'),
  saveEmailConfig: (data) => api.put('/facturacion/email-config', data),
  enviarFactura: (edificioId, unidadId, periodo) => api.post(`/facturacion/${edificioId}/${unidadId}/enviar-factura`, null, { params: { periodo } }),
  anularMorosidad: (edificioId, unidadId, periodo) => api.post(`/facturacion/${edificioId}/${unidadId}/anular-morosidad`, null, { params: { periodo } }),
  uploadFactura: (edificioId, unidadId, periodo, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/facturacion/${edificioId}/${unidadId}/upload-factura?periodo=${periodo}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFacturaPdfUrl: (facturaId) => {
    const token = localStorage.getItem('auth_token');
    return `${BACKEND_URL}/api/facturacion/factura-pdf/${facturaId}?token=${token}`;
  },
  deleteFactura: (edificioId, unidadId, periodo) => api.delete(`/facturacion/${edificioId}/${unidadId}/factura`, { params: { periodo } }),
  addCobroParcial: (edificioId, unidadId, periodo, data) => api.post(`/facturacion/${edificioId}/${unidadId}/cobro-parcial?periodo=${periodo}`, data),
  deleteCobroParcial: (edificioId, unidadId, cobroId, periodo) => api.delete(`/facturacion/${edificioId}/${unidadId}/cobro-parcial/${cobroId}?periodo=${periodo}`),
  sincronizarIngresosLiquidacion: (edificioId, periodo) => api.post(`/facturacion/${edificioId}/sincronizar-ingresos-liquidacion?periodo=${periodo}`),
  getAjustesManuales: (contratoId) => api.get(`/facturacion/contratos/${contratoId}/ajustes-manuales`),
};

// Colppy API
export const colppyApi = {
  createInvoice: (data) => api.post('/colppy/create-invoice', data),
  getInvoicePdfUrl: (idFactura, idCliente) => 
    `${BACKEND_URL}/api/colppy/invoice-pdf?idFactura=${idFactura}&idCliente=${idCliente}`,
  getInvoicePdfBlob: (idFactura, idCliente) =>
    api.get('/colppy/invoice-pdf', { params: { idFactura, idCliente }, responseType: 'blob' }),
  relinkInvoice: (edificioId, unidadId, periodo) =>
    api.post('/colppy/relink-invoice', { edificio_id: edificioId, unidad_id: unidadId, periodo }),
  // Conciliación
  getCuentasBancarias: () => api.get('/colppy/cuentas-bancarias'),
  getMovimientosBancarios: (params) => api.get('/colppy/movimientos-bancarios', { params }),
  // Sincronización de cobros
  sincronizarCobros: (edificioId, periodo) => api.post(`/colppy/sincronizar-cobros/${edificioId}?periodo=${periodo}`),
};

// Conciliación API
export const conciliacionApi = {
  getEstado: (edificioId, periodo) => api.get(`/conciliacion/${edificioId}`, { params: { periodo } }),
  aplicar: (data) => api.post('/conciliacion/aplicar', data),
  eliminar: (id) => api.delete(`/conciliacion/${id}`),
};

// Contratos API
export const contratosApi = {
  list: (params) => api.get('/contratos', { params }),
  get: (id) => api.get(`/contratos/${id}`),
  calcularMontoActualizado: (data) => api.post('/contratos/calcular-monto-actualizado', data),
  create: (data) => api.post('/contratos', data),
  update: (id, data) => api.put(`/contratos/${id}`, data),
  finalizar: (id, fechaBaja) => api.put(`/contratos/${id}/finalizar`, { fecha_baja: fechaBaja || null }),
  editarFechaBaja: (id, fechaBaja) => api.put(`/contratos/${id}/fecha-baja`, { fecha_baja: fechaBaja }),
  delete: (id) => api.delete(`/contratos/${id}`),
  downloadArchivo: (archivoId) => `${BACKEND_URL}/api/archivos-contratos/${archivoId}`,
  // Descarga de un documento asociado a una etapa del contrato (nueva carga multi-archivo).
  // Incluye el token en la query para funcionar con <a href> / window.open.
  downloadEtapaArchivo: (contratoId, etapaId, archivoId) => {
    const token = localStorage.getItem('auth_token');
    return `${BACKEND_URL}/api/contratos/${contratoId}/etapas/${etapaId}/archivos/${archivoId}/download?token=${token}`;
  },
  analizarAdenda: (contratoId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/contratos/${contratoId}/adenda/analizar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  calcularAlquiler: (id, periodo) => api.get(`/contratos/${id}/calcular-alquiler`, { params: { periodo } }),
  calcularMonto: (params) => api.get('/contratos/calcular-monto', { params }),
  // Carga masiva con IA
  cargaMasivaIA: (edificioId, files) => {
    const formData = new FormData();
    formData.append('edificio_id', edificioId);
    files.forEach(file => formData.append('files', file));
    return api.post('/contratos/carga-masiva-ia', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Borradores
  listBorradores: (params) => api.get('/contratos/borradores', { params }),
  getBorrador: (id) => api.get(`/contratos/borradores/${id}`),
  updateBorrador: (id, data) => api.put(`/contratos/borradores/${id}`, data),
  aprobarBorrador: (id) => api.post(`/contratos/borradores/${id}/aprobar`),
  descartarBorrador: (id) => api.delete(`/contratos/borradores/${id}`),
};

// Etapas contractuales (Documentos)
export const etapasContractualesApi = {
  list: (contratoId) => api.get(`/contratos/${contratoId}/etapas`),
  create: (contratoId, data) => api.post(`/contratos/${contratoId}/etapas`, data),
  update: (contratoId, etapaId, data) => api.put(`/contratos/${contratoId}/etapas/${etapaId}`, data),
  delete: (contratoId, etapaId) => api.delete(`/contratos/${contratoId}/etapas/${etapaId}`),
  uploadArchivo: (contratoId, etapaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/contratos/${contratoId}/etapas/${etapaId}/archivos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadUrl: (contratoId, etapaId, archivoId) =>
    `${BACKEND_URL}/api/contratos/${contratoId}/etapas/${etapaId}/archivos/${archivoId}/download`,
  deleteArchivo: (contratoId, etapaId, archivoId) =>
    api.delete(`/contratos/${contratoId}/etapas/${etapaId}/archivos/${archivoId}`),
};

// Depósitos API
export const depositosApi = {
  list: (params) => api.get('/depositos', { params }),
  get: (id) => api.get(`/depositos/${id}`),
  create: (data) => api.post('/depositos', data),
  update: (id, data) => api.put(`/depositos/${id}`, data),
  delete: (id) => api.delete(`/depositos/${id}`),
  aplicarDiferencia: (id, data) => api.post(`/depositos/${id}/aplicar-diferencia`, data),
  uploadDocumento: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/depositos/${id}/documento`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocumento: (depositoId, documentoId) => 
    `${BACKEND_URL}/api/depositos/${depositoId}/documento/${documentoId}`,
};

// Seed API
export const seedApi = {
  seed: () => api.post('/seed'),
};

// Backups v2 (solo rol=admin)
export const backupsApi = {
  modules: () => api.get('/admin/backups/modules'),
  export: (module, format = 'json') => {
    const fd = new FormData();
    fd.append('module', module);
    fd.append('format', format);
    return api.post('/admin/backups/export', fd, {
      responseType: 'blob',
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  dryRun: (file, mode = 'migracion') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', mode);
    return api.post('/admin/backups/dry-run', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  import: (dryRunId, confirmRestauracion = false) => {
    const fd = new FormData();
    fd.append('dry_run_id', dryRunId);
    fd.append('confirm_restauracion_exacta', String(confirmRestauracion));
    return api.post('/admin/backups/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  history: (limit = 100) => api.get('/admin/backups/history', { params: { limit } }),
  restoreSnapshot: (snapshotId) => api.post(`/admin/backups/restore-snapshot/${snapshotId}`),
  // Backups completos → object storage
  fullList: () => api.get('/admin/backups/full'),
  fullCreate: () => api.post('/admin/backups/full/create'),
  fullDownload: (archiveId) => api.get(`/admin/backups/full/${archiveId}/download`, { responseType: 'blob' }),
  fullRestore: (archiveId) => {
    const fd = new FormData();
    fd.append('confirm', 'RESTAURAR');
    return api.post(`/admin/backups/full/${archiveId}/restore`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Legacy Backup API (solo admin) — deprecada, mantener por compat
export const backupApi = {
  exportar: () => api.get('/backup/exportar'),
  importar: (file, mode = 'merge') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/backup/importar?mode=${mode}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// AFIP Facturación Electrónica API
export const afipApi = {
  // Configuración
  getConfig: () => api.get('/afip/config'),
  updateConfig: (data) => api.put('/afip/config', data),
  uploadCertificado: (file, password) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('password', password);
    return api.post('/afip/certificado', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Test de conexión
  testConexion: () => api.post('/afip/test-conexion'),
  
  // Facturas
  emitirFactura: (data) => api.post('/afip/factura', data),
  listFacturas: (params) => api.get('/afip/facturas', { params }),
  getFactura: (id) => api.get(`/afip/factura/${id}`),
  consultarComprobante: (tipoCbte, cbteNro) => api.get(`/afip/consultar/${tipoCbte}/${cbteNro}`),
};

// IA API
export const iaApi = {
  analizarEscritura: (file, unidadId, guardarArchivo = true) => {
    const formData = new FormData();
    formData.append('file', file);
    if (unidadId) formData.append('unidad_id', unidadId);
    formData.append('guardar_archivo', guardarArchivo ? 'true' : 'false');
    return api.post('/ia/analizar-escritura', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getEscritura: (unidadId) => api.get(`/unidades/${unidadId}/escritura`),
  downloadEscrituraUrl: (unidadId) => `${BACKEND_URL}/api/unidades/${unidadId}/escritura/download`,
  deleteEscritura: (unidadId) => api.delete(`/unidades/${unidadId}/escritura`),
  // Carta Oferta (independiente de la escritura)
  uploadCartaOferta: (unidadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/unidades/${unidadId}/carta-oferta`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getCartaOferta: (unidadId) => api.get(`/unidades/${unidadId}/carta-oferta`),
  downloadCartaOfertaUrl: (unidadId) => `${BACKEND_URL}/api/unidades/${unidadId}/carta-oferta/download`,
  deleteCartaOferta: (unidadId) => api.delete(`/unidades/${unidadId}/carta-oferta`),
  // Posesión (independiente de la escritura y la carta oferta)
  uploadPosesion: (unidadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/unidades/${unidadId}/posesion`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPosesion: (unidadId) => api.get(`/unidades/${unidadId}/posesion`),
  downloadPosesionUrl: (unidadId) => `${BACKEND_URL}/api/unidades/${unidadId}/posesion/download`,
  deletePosesion: (unidadId) => api.delete(`/unidades/${unidadId}/posesion`),
};

// ============ Global Citizen — CxC y Cobros ============
export const gcApi = {
  listCxC: (params = {}) => api.get('/gc/cxc', { params }),
  createCxC: (data) => api.post('/gc/cxc', data),
  updateCxC: (cxcId, data) => api.put(`/gc/cxc/${cxcId}`, data),
  anularCxC: (cxcId) => api.post(`/gc/cxc/${cxcId}/anular`),
  registrarCobro: (cxcId, data) => api.post(`/gc/cxc/${cxcId}/cobros`, data),
  listCobrosCxC: (cxcId) => api.get(`/gc/cxc/${cxcId}/cobros`),
  anularCobro: (cobroId) => api.delete(`/gc/cobros/${cobroId}`),
  listCobros: (params = {}) => api.get('/gc/cobros', { params }),
  syncComisiones: (edificioId) => api.post('/gc/cxc/sync-comisiones', null, { params: edificioId ? { edificio_id: edificioId } : {} }),
  syncFeesForesta: (periodo, edificioId) => api.post('/gc/cxc/sync-fees-foresta', null, { params: { ...(periodo ? { periodo } : {}), ...(edificioId ? { edificio_id: edificioId } : {}) } }),
  syncFeesTE: (periodo) => api.post('/gc/cxc/sync-fees-te', null, { params: periodo ? { periodo } : {} }),
  listEgresos: (params = {}) => api.get('/gc/egresos', { params }),
  clasificarMovimiento: (movId, data) => api.put(`/gc/egresos/${movId}/clasificacion`, data),
  clasificarMasivo: (data) => api.post('/gc/egresos/clasificacion-masiva', data),
  resumen: (periodo) => api.get('/gc/resumen', { params: periodo ? { periodo } : {} }),
};

// Leads (CRM Comercial)
export const leadsApi = {
  list: () => api.get('/leads'),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  changeEstado: (id, data) => api.patch(`/leads/${id}/estado`, data),
  changeResultado: (id, data) => api.patch(`/leads/${id}/resultado`, data),
  remove: (id) => api.delete(`/leads/${id}`),
  addActivity: (id, data) => api.post(`/leads/${id}/actividades`, data),
};

// Contactos (Base de Datos comercial)
export const contactosApi = {
  list: () => api.get('/contactos'),
  get: (id) => api.get(`/contactos/${id}`),
  create: (data) => api.post('/contactos', data),
  update: (id, data) => api.put(`/contactos/${id}`, data),
  remove: (id, force = false) => api.delete(`/contactos/${id}${force ? '?force=true' : ''}`),
  checkDuplicate: (data) => api.post('/contactos/check-duplicate', data),
  activarTablero: (id) => api.post(`/contactos/${id}/activar-tablero`),
  noContactar: (id) => api.post(`/contactos/${id}/no-contactar`),
  importParse: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/contactos/import-parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importApply: (data) => api.post('/contactos/import-apply', data),
  listImports: () => api.get('/contactos-imports'),
};

// Veraz (PDFs de informes)
export const verazApi = {
  contratosResumen: () => api.get('/veraz/contratos-resumen'),
  listByContrato: (contrato_id) => api.get(`/veraz/by-contrato/${contrato_id}`),
  listByReserva: (reserva_id) => api.get(`/veraz/by-reserva/${reserva_id}`),
};

// Cowork Foresta API (pool administrado — exclusivo edificio Foresta)
export const coworkForestaApi = {
  getConfig: (edificioId) => api.get(`/cowork-foresta/${edificioId}/config`),
  updateConfig: (edificioId, data) => api.put(`/cowork-foresta/${edificioId}/config`, data),
  listPuestos: (edificioId) => api.get(`/cowork-foresta/${edificioId}/puestos`),
  setAdministracion: (edificioId, unidadId, data) =>
    api.patch(`/cowork-foresta/${edificioId}/puestos/${unidadId}/administracion`, data),
  // Resumen económico mensual (criterio pool)
  resumenMensual: (edificioId, periodo) =>
    api.get(`/cowork-foresta/${edificioId}/resumen-mensual`, { params: { periodo } }),
  // Gastos del cowork
  listGastos: (edificioId, periodo) =>
    api.get(`/cowork-foresta/${edificioId}/gastos`, { params: { periodo } }),
  crearGasto: (edificioId, data, file) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') fd.append(k, v);
    });
    if (file) fd.append('file', file);
    return api.post(`/cowork-foresta/${edificioId}/gastos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  editarGasto: (edificioId, gastoId, data, file) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v !== null && v !== undefined) fd.append(k, v);
    });
    if (file) fd.append('file', file);
    return api.patch(`/cowork-foresta/${edificioId}/gastos/${gastoId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  anularGasto: (edificioId, gastoId) =>
    api.post(`/cowork-foresta/${edificioId}/gastos/${gastoId}/anular`),
  comprobanteUrl: (archivoId) => `${API_BASE}/foresta-ops/archivo/${archivoId}`,
  // Liquidación trimestral (rendición del pool)
  getLiquidacionTrimestral: (edificioId, trimestreKey) =>
    api.get(`/cowork-foresta/${edificioId}/liquidacion-trimestral`, { params: { trimestre_key: trimestreKey } }),
  listLiquidacionesTrimestrales: (edificioId) =>
    api.get(`/cowork-foresta/${edificioId}/trimestres-guardados`),
  rendicionInversores: (edificioId, trimestreKey) =>
    api.get(`/cowork-foresta/${edificioId}/rendicion-inversores`, { params: { trimestre_key: trimestreKey } }),
  saveLiquidacionTrimestral: (edificioId, data) =>
    api.put(`/cowork-foresta/${edificioId}/liquidacion-trimestral`, data),
  publicarLiquidacionTrimestral: (edificioId, trimestreKey, publicar) =>
    api.post(`/cowork-foresta/${edificioId}/liquidacion-trimestral/publicar`, { trimestre_key: trimestreKey, publicar }),
  // Portal inversor: liquidaciones trimestrales publicadas
  misLiquidacionesTrimestrales: () =>
    api.get(`/cowork-foresta/mi-cowork/liquidaciones-trimestrales`),
};

export default api;
